import {useState} from "react";
import {useSearchParams} from "react-router";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {DropdownAction, DropdownActionItem, DropdownItem} from "@/components/quickActionsBar/DropdownAction.tsx";
import {DropdownDivider} from "@/components/quickActionsBar/DropdownDivider.tsx";
import {QuickActionsBar} from "@/components/quickActionsBar/QuickActionsBar.tsx";
import {IntegrationFormData, IntegrationListData} from "@/lib/types/integrations.ts";
import {IntegrationActionDelete} from "@/pages/integrations/list/integrationsActive/actions/IntegrationActionDelete.tsx";
import {IntegrationActionEdit} from "@/pages/integrations/list/integrationsActive/actions/IntegrationActionEdit.tsx";
import {IntegrationActionTest} from "@/pages/integrations/list/integrationsActive/actions/IntegrationActionTest.tsx";
import {IntegrationLogsModal} from "@/pages/integrations/log/IntegrationLogsModal.tsx";
import {TestIntegrationModal} from "@/pages/integrations/test/TestIntegrationModal.tsx";

type IntegrationActionsProps = {
  integration: IntegrationListData;
};

export const IntegrationActions = ({integration}: IntegrationActionsProps) => {
  const {user: currentUser} = useCurrentUserContext();
  const [queryParams, setQueryParams] = useSearchParams();

  const [showEditIntegrationModalAction, setShowEditIntegrationModalAction] = useState(false);
  const [showIntegrationActionTest, setShowIntegrationActionTest] = useState(false);
  const [showIntegrationActionLogs, setShowIntegrationActionLogs] = useState(
    queryParams.get("err") === integration.id.toString()
  );
  const [showDropdownActionItem, setShowDropdownActionItem] = useState(false);
  const [savedIntegration, setSavedIntegration] = useState<IntegrationFormData | null>(null);

  const openEdit = () => {
    setShowIntegrationActionTest(false);
    setSavedIntegration(null);
    setShowEditIntegrationModalAction(true);
  };

  const dropdownItems: DropdownItem[] = [];

  if (currentUser!.has_write_access)
    dropdownItems.push(<DropdownActionItem text="Edit" onClick={() => setShowEditIntegrationModalAction(true)} />);

  dropdownItems.push(<DropdownActionItem text="Test" onClick={() => setShowIntegrationActionTest(true)} />);
  dropdownItems.push(<DropdownActionItem text="Error Log" onClick={() => setShowIntegrationActionLogs(true)} />);

  if (currentUser!.has_write_access) {
    dropdownItems.push(<DropdownDivider />);
    dropdownItems.push(<DropdownActionItem text="Delete" onClick={() => setShowDropdownActionItem(true)} />);
  }

  return (
    <>
      <QuickActionsBar
        id={`user-action-${integration.id}`}
        actions={[<DropdownAction id={`user-${integration.id}`} items={dropdownItems} />]}
      />

      <IntegrationActionEdit
        integration={integration}
        show={showEditIntegrationModalAction}
        setShow={setShowEditIntegrationModalAction}
        setNewIntegration={setSavedIntegration}
      />
      <IntegrationActionTest
        integration={integration}
        show={showIntegrationActionTest}
        setShow={setShowIntegrationActionTest}
        onEdit={openEdit}
      />
      {!!savedIntegration && (
        <TestIntegrationModal
          show={!!savedIntegration}
          hide={() => setSavedIntegration(null)}
          integration={savedIntegration}
          onEdit={openEdit}
        />
      )}
      <IntegrationLogsModal
        show={showIntegrationActionLogs}
        hide={() => {
          setShowIntegrationActionLogs(false);
          const params = new URLSearchParams(queryParams);
          params.delete("err");
          setQueryParams(params);
        }}
        integration={integration}
      />
      <IntegrationActionDelete
        integration={integration}
        show={showDropdownActionItem}
        setShow={setShowDropdownActionItem}
      />
    </>
  );
};
